import { assign, fromPromise, setup } from "xstate";
import type { ComponentType, EmbedConfig } from "../config/types";

export interface EmbedConfigContext {
  instanceId: string;
  expectedType: ComponentType;
  config: EmbedConfig | null;
  error: string | null;
}

export type EmbedConfigEvent =
  | {
      type: "config.load";
      instanceId: string;
      expectedType: ComponentType;
    }
  | {
      type: "config.provided";
      config: EmbedConfig;
      expectedType: ComponentType;
    };

export class EmbedConfigTypeError extends Error {
  constructor(
    readonly actualType: ComponentType,
    readonly expectedType: ComponentType,
  ) {
    super(`Embed config is "${actualType}", expected "${expectedType}"`);
    this.name = "EmbedConfigTypeError";
  }
}

export function createEmbedConfigMachine(
  loadConfig: (instanceId: string, signal: AbortSignal) => Promise<EmbedConfig>,
) {
  return setup({
    types: {
      context: {} as EmbedConfigContext,
      events: {} as EmbedConfigEvent,
    },
    actors: {
      loadConfig: fromPromise<
        EmbedConfig,
        { instanceId: string; expectedType: ComponentType }
      >(async ({ input, signal }) => {
        const config = await loadConfig(input.instanceId, signal);
        if (config.componentType !== input.expectedType) {
          throw new EmbedConfigTypeError(
            config.componentType,
            input.expectedType,
          );
        }
        return config;
      }),
    },
    actions: {
      assignRequest: assign(
        (
          { context },
          params: { instanceId: string; expectedType: ComponentType },
        ) => ({
          ...context,
          instanceId: params.instanceId,
          expectedType: params.expectedType,
          config: null,
          error: null,
        }),
      ),
      assignConfig: assign({
        config: (_, params: { config: EmbedConfig }) => params.config,
        error: null,
      }),
      assignError: assign({
        config: null,
        error: (_, params: { error: string }) => params.error,
      }),
    },
    guards: {
      providedTypeMatches: (
        _,
        params: { config: EmbedConfig; expectedType: ComponentType },
      ) => params.config.componentType === params.expectedType,
    },
  }).createMachine({
    id: "embedConfig",
    initial: "idle",
    context: {
      instanceId: "",
      expectedType: "schedule",
      config: null,
      error: null,
    },
    on: {
      "config.provided": [
        {
          guard: {
            type: "providedTypeMatches",
            params: ({ event }) => ({
              config: event.config,
              expectedType: event.expectedType,
            }),
          },
          target: ".ready",
          actions: {
            type: "assignConfig",
            params: ({ event }) => ({ config: event.config }),
          },
        },
        {
          target: ".failed",
          actions: {
            type: "assignError",
            params: ({ event }) => ({
              error: new EmbedConfigTypeError(
                event.config.componentType,
                event.expectedType,
              ).message,
            }),
          },
        },
      ],
      "config.load": {
        target: ".loading",
        reenter: true,
        actions: {
          type: "assignRequest",
          params: ({ event }) => ({
            instanceId: event.instanceId,
            expectedType: event.expectedType,
          }),
        },
      },
    },
    states: {
      idle: {},
      loading: {
        invoke: {
          src: "loadConfig",
          input: ({ context }) => ({
            instanceId: context.instanceId,
            expectedType: context.expectedType,
          }),
          onDone: {
            target: "ready",
            actions: {
              type: "assignConfig",
              params: ({ event }) => ({ config: event.output }),
            },
          },
          onError: {
            target: "failed",
            actions: {
              type: "assignError",
              params: ({ event }) => ({
                error:
                  event.error instanceof Error
                    ? event.error.message
                    : "Unable to load this embed",
              }),
            },
          },
        },
      },
      ready: {},
      failed: {},
    },
  });
}
